/**
 * Floor names: reading a level out of one, and writing the next one the way the
 * building already does it.
 *
 * German floor names carry their level in the text — `3.OG` is the third upper
 * floor, `2.UG` the second basement — but every office writes it differently:
 * `1.OG`, `01. OG`, `OG1`, `OG 01`. All of them are correct, and a portal that
 * imposes its own spelling on a building whose plans, signage and keys say
 * something else creates a mismatch someone has to reconcile by hand.
 *
 * So: the level is derived from whatever was typed, and a new floor is named the
 * way the floors above and below it are named. The house default — `1.OG` — only
 * applies to the first floor of an empty building, where there is no convention
 * to follow yet.
 */

/** How this building spells its floor names. */
export interface FloorNamePattern {
  /** `suffix` = `1.OG`, `prefix` = `OG1`. */
  order: 'suffix' | 'prefix';
  /** Digits, so `01.OG` keeps its leading zero. */
  digits: number;
  /** What sits between the number and the abbreviation, e.g. `.`, `. `, ` `, ``. */
  separator: string;
}

/** `1.OG` — the common German form, used when a building has nothing to imitate. */
export const DEFAULT_PATTERN: FloorNamePattern = {
  order: 'suffix',
  digits: 1,
  separator: '.',
};

export interface ParsedFloorName {
  level: number;
  pattern: FloorNamePattern;
}

/**
 * `17. OG` → 17, `UG03` → −3, `EG` → 0.
 *
 * Returns `null` for anything that is not a floor designation — `Dachgeschoss`,
 * `Halle West`, `2. Etage`. A name we cannot read must not produce a level, since
 * a wrong level is worse than none: it decides sort order and, through the
 * severity context, which rules a hazard is judged by.
 */
export const parseFloorName = (raw: string | null | undefined): ParsedFloorName | null => {
  const name = (raw ?? '').trim();
  if (!name) return null;

  // Ground floor: no number to read, and no pattern to learn from it either.
  if (/^(EG|E\.?G\.?|Erdgeschoss)$/i.test(name)) {
    return { level: 0, pattern: DEFAULT_PATTERN };
  }

  // `1.OG`, `01. OG`, `17 OG`
  const suffix = name.match(/^(\d{1,3})(\s*\.\s*|\s+|\s*)(OG|UG)$/i);
  if (suffix) {
    const [, digitsRaw, separator, marker] = suffix;
    return {
      level: signedLevel(marker, digitsRaw),
      pattern: { order: 'suffix', digits: digitsRaw.length, separator: normaliseSeparator(separator) },
    };
  }

  // `OG1`, `OG 01`, `UG3`
  const prefix = name.match(/^(OG|UG)(\s*\.\s*|\s+|\s*)(\d{1,3})$/i);
  if (prefix) {
    const [, marker, separator, digitsRaw] = prefix;
    return {
      level: signedLevel(marker, digitsRaw),
      pattern: { order: 'prefix', digits: digitsRaw.length, separator: normaliseSeparator(separator) },
    };
  }

  return null;
};

const signedLevel = (marker: string, digits: string): number => {
  const value = Number(digits);
  return marker.toUpperCase() === 'UG' ? -value : value;
};

/**
 * Keep the spacing as typed, but collapse runs of whitespace: `01 .  OG` and
 * `01. OG` are the same intent, and reproducing the sloppier one would look like
 * a bug rather than fidelity.
 */
const normaliseSeparator = (separator: string): string => {
  if (!separator) return '';
  // A dot may or may not be followed by a space, and that difference is the
  // whole point of imitating the house style — `01.OG` and `01. OG` are both
  // conventions somebody chose.
  if (separator.includes('.')) return /\.\s/.test(separator) ? '. ' : '.';
  return ' ';
};

/**
 * The convention this building follows, taken from the floor names it already
 * has.
 *
 * The most frequent pattern wins, because a single typo should not redefine the
 * house style. Ties go to the more recent — later floors were named later, and a
 * building being renamed converges on the new spelling.
 */
export const inferFloorNamePattern = (names: (string | null | undefined)[]): FloorNamePattern => {
  const counts = new Map<string, { pattern: FloorNamePattern; count: number; lastIndex: number }>();

  names.forEach((name, index) => {
    const parsed = parseFloorName(name);
    // The ground floor carries no pattern — `EG` says nothing about how the
    // upper floors are written.
    if (!parsed || parsed.level === 0) return;

    const key = `${parsed.pattern.order}|${parsed.pattern.digits}|${parsed.pattern.separator}`;
    const entry = counts.get(key);
    if (entry) {
      entry.count += 1;
      entry.lastIndex = index;
    } else {
      counts.set(key, { pattern: parsed.pattern, count: 1, lastIndex: index });
    }
  });

  if (counts.size === 0) return DEFAULT_PATTERN;

  // `Array.from`, not a spread: the build target does not allow iterating a Map
  // iterator directly (TS2802).
  return Array.from(counts.values()).sort(
    (a, b) => b.count - a.count || b.lastIndex - a.lastIndex
  )[0].pattern;
};

/** Level 0 has its own name; `0.OG` is not a thing anyone writes. */
export const formatFloorName = (level: number, pattern: FloorNamePattern): string => {
  if (level === 0) return 'EG';

  const marker = level < 0 ? 'UG' : 'OG';
  const digits = String(Math.abs(level)).padStart(pattern.digits, '0');

  return pattern.order === 'prefix'
    ? `${marker}${pattern.separator}${digits}`
    : `${digits}${pattern.separator}${marker}`;
};

/**
 * Level and name for the floor to add next.
 *
 * Upwards from the highest floor present, because that is what "add a floor"
 * means in a building that has some — basements are dug once and rarely by
 * clicking a plus. An empty building starts at the ground floor, which is where
 * a building starts.
 */
export const nextFloor = (
  existing: { name?: string | null; level?: number | null }[]
): { level: number; name: string } => {
  const pattern = inferFloorNamePattern(existing.map((floor) => floor.name));

  const levels = existing
    .map((floor) =>
      typeof floor.level === 'number' ? floor.level : parseFloorName(floor.name)?.level
    )
    .filter((level): level is number => typeof level === 'number' && Number.isFinite(level));

  // When no floors have parseable levels but floors exist (e.g., "Halle West"),
  // stack above them rather than defaulting to ground and creating a duplicate.
  const level = levels.length === 0 ? (existing.length > 0 ? existing.length : 0) : Math.max(...levels) + 1;

  return { level, name: formatFloorName(level, pattern) };
};
